/**
 * Frontend payment functions for communicating with the backend payment system
 * This file contains all functions that handle payment processing through
 * the SimplyCoach payment backend infrastructure.
 */

import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebaseFunctions";

/**
 * Initiates a payment transaction with the Pelecard payment gateway
 *
 * This function communicates with the backend cloud function to create a payment
 * transaction and returns the iframe URL for payment processing. The function
 * handles all payment gateway configuration through the backend.
 *
 * @async
 * @function InitTransaction
 * @param {Object} transactionData - Transaction configuration data
 * @param {string} transactionData.transactionType - Transaction type: "j2" (registration), "j4" (charge), "j5" (authorization), "j9" (delayed charge)
 * @param {number} transactionData.total - Payment amount in agorot (e.g., 32500 for 325.00 ILS)
 * @returns {Promise<Object>} Promise that resolves to the transaction response
 * @throws {Error} When the request fails or returns an error response
 *
 * @example
 * // Regular charge payment (J4)
 * try {
 *   const result = await InitTransaction({
 *     transactionType: "j4",
 *     total: 32500
 *   });
 *   console.log("Payment URL:", result.data.iframeURL);
 * } catch (error) {
 *   console.error("Payment failed:", error.message);
 * }
 *
 * @example
 * // Credit card registration and token creation (J2)
 * try {
 *   const result = await InitTransaction({
 *     transactionType: "j2",
 *     total: 100
 *   });
 *   console.log("Registration URL:", result.data.iframeURL);
 * } catch (error) {
 *   console.error("Registration failed:", error.message);
 * }
 */
export const InitTransaction = async (transactionData) => {
  try {
    // Validate input parameters
    if (!transactionData) {
      throw new Error("Transaction data is required");
    }

    const { transactionType, total } = transactionData;

    // Validate transaction type
    if (!transactionType) {
      throw new Error("Transaction type is required");
    }

    if (!["j2", "j4", "j5", "j9"].includes(transactionType)) {
      throw new Error(
        "Invalid transaction type. Must be 'j2', 'j4', 'j5', or 'j9'"
      );
    }

    // Validate total amount (not required for J2 registration)
    if (transactionType !== "j2") {
      if (!total || typeof total !== "number" || total <= 0) {
        throw new Error("Total must be a positive number in agorot");
      }
    }

    // Prepare request payload
    const payload = {
      transactionType,
    };

    // Add total only for non-J2 transactions
    if (transactionType !== "j2" && total) {
      payload.total = total;
    }


    // Make request to backend cloud function
    const response = await fetch(
      "https://inittransaction-543qzhi62a-uc.a.run.app",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );



    // Check if the request was successful
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (parseError) {
        // If we can't parse the error response, use the status text
        console.warn("Could not parse error response:", parseError);
      }

      throw new Error(errorMessage);
    }

    // Parse response
    let responseData;
    try {
      responseData = await response.json();
    } catch (parseError) {
      throw new Error("Invalid response format from payment gateway");
    }

    // Check if the backend operation was successful
    if (!responseData.success) {
      throw new Error(responseData.error || "Transaction initiation failed");
    }

    // Return successful response
    return {
      success: true,
      data: responseData.data,
      message: responseData.message,
      transactionType: responseData.transactionType,
      actionType: responseData.actionType,
      total: responseData.total,
      createToken: responseData.createToken,
      registrationDocId: responseData.registrationDocId,
    };
  } catch (error) {
    // Re-throw the error with additional context
    if (error.name === "TypeError" && error.message.includes("fetch")) {
      throw new Error(
        `Network error: Unable to connect to payment gateway. Please check your internet connection.`
      );
    } else if (error.message.includes("403")) {
      throw new Error(
        `Access denied (403): The payment gateway rejected the request. This might be due to CORS restrictions or authentication issues.`
      );
    } else if (error.message.includes("CORS")) {
      throw new Error(
        `CORS error: The payment gateway is blocking cross-origin requests. Please contact support.`
      );
    } else {
      throw new Error(`Payment transaction failed: ${error.message}`);
    }
  }
};

/**
 * Test function to check if the payment gateway is accessible
 * This helps debug connection and CORS issues
 */
export const testPaymentGateway = async () => {
  try {

    // Test with a simple GET request first
    const testResponse = await fetch(
      "https://inittransaction-543qzhi62a-uc.a.run.app",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    console.log("Test response:", {
      status: testResponse.status,
      statusText: testResponse.statusText,
      headers: Object.fromEntries(testResponse.headers.entries()),
    });

    return {
      success: true,
      status: testResponse.status,
      message: "Gateway is accessible",
    };
  } catch (error) {
    console.error("Payment gateway test failed:", error);
    return {
      success: false,
      error: error.message,
      message: "Gateway test failed",
    };
  }
};

/**
 * Utility function to convert ILS amount to agorot
 *
 * @param {number} ilsAmount - Amount in Israeli Shekels (e.g., 325.50)
 * @returns {number} Amount in agorot (e.g., 32550)
 */
export const convertILSToAgorot = (ilsAmount) => {
  return Math.round(ilsAmount * 100);
};

/**
 * Utility function to convert agorot to ILS amount
 *
 * @param {number} agorotAmount - Amount in agorot (e.g., 32550)
 * @returns {number} Amount in Israeli Shekels (e.g., 325.50)
 */
export const convertAgorotToILS = (agorotAmount) => {
  return agorotAmount / 100;
};

/**
 * Transaction type constants for better code readability
 */
export const TRANSACTION_TYPES = {
  REGISTRATION: "j2", // Credit card registration and token creation
  CHARGE: "j4", // Regular charge payment
  AUTHORIZATION: "j5", // Authorization hold for hotels/car rentals
  DELAYED_CHARGE: "j9", // Delayed charge
};

/**
 * Helper function to get transaction type description
 *
 * @param {string} transactionType - Transaction type code
 * @returns {string} Human-readable description of the transaction type
 */
export const getTransactionTypeDescription = (transactionType) => {
  const descriptions = {
    [TRANSACTION_TYPES.REGISTRATION]: "Credit Card Registration",
    [TRANSACTION_TYPES.CHARGE]: "Regular Charge",
    [TRANSACTION_TYPES.AUTHORIZATION]: "Authorization Hold",
    [TRANSACTION_TYPES.DELAYED_CHARGE]: "Delayed Charge",
  };

  return descriptions[transactionType] || "Unknown Transaction Type";
};

/**
 * Reads the registration document from Firestore to check payment status
 *
 * This function retrieves the registration document from the metadata collection
 * and checks if the payment was successful. It's used after a payment transaction
 * to verify the payment status before proceeding with account creation.
 *
 * @async
 * @function getRegistrationStatus
 * @param {string} registrationDocId - The registration document ID returned from InitTransaction
 * @returns {Promise<Object>} Promise that resolves to the registration document data
 * @throws {Error} When the document doesn't exist or there's a database error
 *
 * @example
 * try {
 *   const registrationData = await getRegistrationStatus("abc123");
 *   if (registrationData.status === "success") {
 *     // Proceed with account creation
 *     console.log("Payment successful, token:", registrationData.token);
 *   } else {
 *     console.log("Payment failed or pending");
 *   }
 * } catch (error) {
 *   console.error("Error checking registration status:", error.message);
 * }
 */
export const getRegistrationStatus = async (registrationDocId) => {
  try {
    if (!registrationDocId) {
      throw new Error("מזהה מסמך הרישום נדרש");
    }

    // Reference to the registration document in the metadata collection
    const registrationRef = doc(db, "metadata", "payments", "registrations", registrationDocId);

    // Get the document
    const registrationDoc = await getDoc(registrationRef);

    if (!registrationDoc.exists()) {
      throw new Error("מסמך הרישום לא נמצא");
    }

    const registrationData = registrationDoc.data();

    return {
      success: true,
      data: registrationData,
      docId: registrationDocId,
    };
  } catch (error) {
    console.error("Error reading registration document:", error);
    throw new Error(`שגיאה בקריאת סטטוס הרישום: ${error.message}`);
  }
};


